import React, { useEffect, useState } from "react";
import Header from "../components/dynamic/Header";
import ActionButton from "../components/dynamic/ActionButton";
import { useAuthStore } from "../store/authStore";
import { usePromptStore } from "../store/promptStore";
import { PromptRequest, PromptTypeKey } from "../types/promptTypes";
import SelectField from "../components/static/fields/SelectField";
import NumberCodeField from "../components/static/fields/NumberCodeField";
import PasswordField from "../components/static/fields/PasswordField";
import TextField from "../components/static/fields/TextField";
import CodeField from "../components/static/fields/CodeField";
import CheckboxField from "../components/static/fields/CheckboxField";
import { Screen } from "../types/enums";
import { submitPrompts, closeSession } from "@plutoxyz/frame-js";
import { CredentialBanner } from "../components/view-specific/CredentialBanner";
import { useDemoStore } from "../store/demoStore";
import LoginField from "../components/dynamic/LoginField";

const CredentialView: React.FC = () => {
  const prompts = usePromptStore((state) => state.prompts);
  const metadata = usePromptStore((state) => state.metadata);
  const areValuesValid = usePromptStore((state) => state.areValuesValid);
  const promptValues = usePromptStore((state) => state.promptValues);
  const setScreen = useAuthStore((state) => state.setScreen);
  const notaryUrl = useDemoStore((state) => state.endpoints.notary);
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => {
    if (metadata.icon) {
      setLogo(`https://logo.clearbit.com/${metadata.icon}`);
    }
  }, [metadata]);

  const handleContinue = async () => {
    if (!areValuesValid) return;

    sessionStorage.removeItem(`${useAuthStore.getState().sessionId}-prompts`);

    try {
      // Todo: gracefully handle 400s
      await submitPrompts(
        metadata.request_id,
        promptValues.map((value) => ({ value })),
        useAuthStore.getState().sessionId,
        notaryUrl
      );

      // add 2 second artificial loading delay before going to pending
      // in case we get next script prompt
      // loading state on continue button
      setScreen(Screen.PENDING);
    } catch (error) {
      await closeSession(useAuthStore.getState().sessionId, notaryUrl);
    }
  };

  const renderInputField = (prompt: PromptRequest, index: number) => {
    switch (prompt.type) {
      case PromptTypeKey.TEXT:
        return (
          <TextField key={prompt.key} index={index} onEnter={handleContinue} />
        );
      case PromptTypeKey.PASSWORD:
        return (
          <PasswordField
            key={prompt.key}
            index={index}
            onEnter={handleContinue}
          />
        );
      case PromptTypeKey.CODE:
        return (
          <CodeField key={prompt.key} index={index} onEnter={handleContinue} />
        );
      case PromptTypeKey.NUMBER_CODE:
        return (
          <NumberCodeField
            key={prompt.key}
            index={index}
            onEnter={handleContinue}
          />
        );
      case PromptTypeKey.SELECT:
        return <SelectField key={prompt.key} index={index} />;
      case PromptTypeKey.CHECKBOX:
        return <CheckboxField key={prompt.key} index={index} />;
      case PromptTypeKey.LOGIN:
        return (
          <LoginField key={prompt.key} index={index} onEnter={handleContinue} />
        );
      default:
        throw new Error(`Unsupported prompt type: ${prompt.type}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {prompts.length !== 1 || prompts[0].type !== PromptTypeKey.NUMBER_CODE ? (
        <CredentialBanner secondLogoUrl={logo} />
      ) : null}
      <Header title={metadata.title} subheader={metadata.description} />
      <div className="w-full mb-6">{prompts.map(renderInputField)}</div>
      {!(prompts.length === 1 && prompts[0].type === PromptTypeKey.LOGIN) && (
        <div className="w-full mb-4 mt-auto">
          <ActionButton
            disabled={!areValuesValid}
            label="Continue"
            onClick={handleContinue}
          />
        </div>
      )}

      {/* <LearnMoreLink text="Login another way" onClick={() => {}} /> */}
    </div>
  );
};

export default CredentialView;
